import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

interface ContactNote {
  contactId: string;
  note: string;
  lastUpdated: string;
}

interface ContactNotesContextType {
  getNote: (contactId: string) => string;
  setNote: (contactId: string, note: string) => void;
  clearNote: (contactId: string) => void;
  getAllNotes: () => ContactNote[];
}

const ContactNotesContext = createContext<ContactNotesContextType | undefined>(
  undefined
);

const STORAGE_KEY = "contact-notes";

// Load notes from localStorage
const loadNotesFromStorage = (): Map<string, ContactNote> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ContactNote[];
      return new Map(parsed.map((note) => [note.contactId, note]));
    }
  } catch (error) {
    console.error("Failed to load notes from storage:", error);
  }
  return new Map();
};

// Save notes to localStorage
const saveNotesToStorage = (notes: Map<string, ContactNote>) => {
  try {
    const notesArray = Array.from(notes.values());
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notesArray));
  } catch (error) {
    console.error("Failed to save notes to storage:", error);
  }
};

interface ContactNotesProviderProps {
  children: ReactNode;
}

export const ContactNotesProvider = ({
  children,
}: ContactNotesProviderProps) => {
  const [notes, setNotes] = useState<Map<string, ContactNote>>(() =>
    loadNotesFromStorage()
  );

  const getNote = useCallback(
    (contactId: string): string => {
      return notes.get(contactId)?.note || "";
    },
    [notes]
  );

  const setNote = useCallback((contactId: string, note: string) => {
    setNotes((prevNotes) => {
      const newNotes = new Map(prevNotes);

      if (note.trim() === "") {
        // Remove note if empty
        newNotes.delete(contactId);
      } else {
        // Update or create note
        newNotes.set(contactId, {
          contactId,
          note,
          lastUpdated: new Date().toISOString(),
        });
      }

      saveNotesToStorage(newNotes);
      return newNotes;
    });
  }, []);

  const clearNote = useCallback((contactId: string) => {
    setNotes((prevNotes) => {
      const newNotes = new Map(prevNotes);
      newNotes.delete(contactId);
      saveNotesToStorage(newNotes);
      return newNotes;
    });
  }, []);

  const getAllNotes = useCallback((): ContactNote[] => {
    return Array.from(notes.values());
  }, [notes]);

  const value: ContactNotesContextType = {
    getNote,
    setNote,
    clearNote,
    getAllNotes,
  };

  return (
    <ContactNotesContext.Provider value={value}>
      {children}
    </ContactNotesContext.Provider>
  );
};

export const useContactNotes = () => {
  const context = useContext(ContactNotesContext);
  if (context === undefined) {
    throw new Error(
      "useContactNotes must be used within a ContactNotesProvider"
    );
  }
  return context;
};
